import MenuList from "@/components/MenuList";
import { IFavorite } from "@/types/menu-types";
import { prefix } from ".";
import Header from "@/components/Header";
import { useParams, useSearchParams } from "react-router-dom";
import { IReducersState } from "@/reducers";
import { useSelector } from "react-redux";
import Business from "@/components/Business";
import { useEffect, useMemo, useRef, useState } from "react";
import Tabs from "@/components/Tabs";
import ShoppingCartBar from "@/components/ShoppingCartBar";
import { IDeliveryAddress, IDiningMethods, IPickUpOption } from "@/types/common-types";
import { createUuid, getLocalStorageJson, setLocalStorageJson } from "@/utils";
import { Constants } from "@/constants";
import DiningMethodsPanel from "@/components/DiningMethodsPanel";
import { ITable } from "@/types/table-types";
import ActionType from "@/actions/action-type";
import { IDispatch } from '@/actions';
import { useDispatch } from "react-redux";
import { ILineItem, IShoppingCartInfo, ListingCountRef } from "@/types/order-types";
import ShoppingCartPopup from "@/components/ShoppingCartPopup";
import Toast from "@/components/Toast";
import SuccessIcon from "@/components/svg/SuccessIcon";
import { getOrderByShoppingCart } from "@/utils/order-utils";
import { getOrderDetailsByEndpoint, overbooking } from "@/interface/order";
import { useTranslation } from "react-i18next";
import { Notify, Popup, Button } from 'react-vant';
import DineInIcon from "@/components/svg/DineInIcon";
import TakeawayIcon from "@/components/svg/TakeawayIcon";
import DiningMethods from "@/components/DiningMethods";
import TipIcon from "@/components/svg/TipIcon";
import Footer from "@/components/Footer";
import { getTableAndQRCodeDetails } from "@/utils/table-utils";
import ScanCodeFailedIcon from "@/components/svg/ScanCodeFailedIcon";
import i18n from "@/i18n";

const MenuListPage = () => {
  const [searchParams] = useSearchParams();
  const dispatch: IDispatch = useDispatch();
  const { businessID = '' } = useParams();
  const { t } = useTranslation('common');
  const qrId = searchParams.get('qr') || '';
  const number = searchParams.get('refNo') || '';
  const common = useSelector((state: IReducersState) => state.common);
  const cart = useSelector((state: IReducersState) => state.cart);
  const { business, menusData } = common;
  const { shoppingCartData: shoppingCartDataState } = cart;
  const { storeID: businessStoreID, id: pBusinessID } = business;
  const [activeKes, setActiveKes] = useState<string[]>([]);
  const [data, setData] = useState<IFavorite[]>([]);
  const [isFirst, setIsFirst] = useState<boolean>(true);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [openPopupRequest, setOpenPopupRequest] = useState(0);
  const [addressList, setAddressList] = useState<IDeliveryAddress[]>([]);
  const [showOption, setShowOption] = useState<boolean>(false);
  const [diningMethod, setDiningMethod] = useState<IDiningMethods>();
  const [showContent, setShowContent] = useState<boolean>(false);
  const [tableDetails, setTableDetails] = useState<ITable>();
  const [pickUpOption, setPickUpOption] = useState<IPickUpOption | undefined>();
  const [shoppingCartData, setShoppingCartData] = useState<IShoppingCartInfo>();
  const [showShoppingCartPopup, setShowShoppingCartPopup] = useState<boolean>(false);
  const [cartList, setCartList] = useState<IFavorite[]>([]);
  const [showTips, setShowTips] = useState<boolean>(false);
  const [showExitConfirm, setShowExitConfirm] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [showErrorPopup, setShowErrorPopup] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (pBusinessID) {
      loadRecords();
    }
    // eslint-disable-next-line
  }, [business, menusData]);

  useEffect(() => {
    setShoppingCartData(shoppingCartDataState[businessStoreID]);
    // eslint-disable-next-line
  }, [shoppingCartDataState]);

  useEffect(() => {
    if (openPopupRequest === 0 || diningMethod === undefined) return;
    setOpenPopupRequest(0);
    setShowPopup(true);
  }, [diningMethod, openPopupRequest]);

  useEffect(() => {
    loadDetails();
    // eslint-disable-next-line
  }, [number, menusData]);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted){
        loadDetails();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
    // eslint-disable-next-line
  }, [number, menusData, businessStoreID]);

  useEffect(() => {
    const { favoriteIdLineItemRef = {} } = shoppingCartData || {};
    const listings = Object.keys(favoriteIdLineItemRef);
    if (showShoppingCartPopup) {
      const records: IFavorite[] = [];
      menusData.forEach(item => {
        item.favorite_section.forEach(record => {
          record.favorites.forEach(favorite => {
            if (listings.includes(`${favorite.id}`)) {
              records.push(favorite);
            }
          })
        })
      });
      setCartList(records);
    } else {
      setCartList([]);
    }
  }, [showShoppingCartPopup, shoppingCartData, menusData]);

  useEffect(() => {
    if (menusData.length > 0 && activeKes.length > 1) {
      const menu = menusData.filter(item => `${item.id}` === activeKes[0])[0];

      if (menu) {
        let favorites: IFavorite[] = [];
        let isFirst = false;
        menu.favorite_section.forEach((item, index) => {
          if (`${item.id}` === activeKes[1]) {
            favorites = item.favorites;
            isFirst = index === 0;
          }
        });
        setIsFirst(isFirst);
        setData(favorites);
      }
    }
  }, [activeKes, menusData]);

  useEffect(() => {
    if (errorMsg) {
      setShowErrorPopup(true);
      const timer = setTimeout(() => {
        setShowErrorPopup(false);
        setErrorMsg('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMsg]);

  const loadDetails = async () => {
    if (menusData.length < 1 || !businessStoreID) return;

    const localCartData = getLocalStorageJson(Constants.SHOPPING_CART_DATA) || {};
    let data = localCartData[businessStoreID] || {};
    let favoriteIdLineItemRef: ListingCountRef = { ...(data.favoriteIdLineItemRef || {}) };
    // 有refNo，则根据refNo获取订单详情转化为购物车
    if (number) {
      const orderDetails = await getOrderDetailsByEndpoint(number, businessID);

      const { line_items: lineItems } = orderDetails?.order || {};

      if (lineItems && Array.isArray(lineItems) && lineItems.length > 0) {
        for (let i = 0; i < lineItems.length; i++) {
          const lineItem: any = lineItems[i];
          let isFind = false;

          for (let k = 0; k < menusData.length; k++) {
            const menu = menusData[k];
            const { favorite_section } = menu;

            for (let j = 0; j < favorite_section.length; j++) {
              const favorite = favorite_section[j]
              const { favorites } = favorite;

              for (let f = 0; f < favorites.length; f++) {
                const item = favorites[f];
                const { listing_id, id } = item;

                if (lineItem.purchasable_id === listing_id) {
                  favoriteIdLineItemRef[id] = {
                    uuid: createUuid(),
                    purchasable_type: lineItem.purchasable_type || 'Listing',
                    purchasable_id: lineItem.purchasable_id,
                    price: lineItem.price,
                    quantity: lineItem.quantity,
                    total: lineItem.total,
                    label: lineItem.label,
                  } as ILineItem;
                  isFind = true;
                  break;
                }
              }

              if (isFind) break;
            }

            if (isFind) break;
          }
        }
      }
    } else {
      // 没有refNo，则刷新购物车item uuid
      const refreshedFavoriteIdLineItemRef: ListingCountRef = {};
      Object.keys(favoriteIdLineItemRef).forEach((favoriteId) => {
        const item = favoriteIdLineItemRef[favoriteId];
        refreshedFavoriteIdLineItemRef[favoriteId] = {
          ...item,
          uuid: createUuid(),
        };
      });
      favoriteIdLineItemRef = refreshedFavoriteIdLineItemRef;
    };

    data.favoriteIdLineItemRef = favoriteIdLineItemRef;
    const newShoppingCartData = { ...localCartData };
    newShoppingCartData[businessStoreID] = data;

    dispatch({
      type: ActionType.SET_SHOPPING_CART_DATA,
      data: newShoppingCartData,
    });

    setShoppingCartData(data);
  };

  const loadRecords = async () => {
    if (menusData && menusData.length > 0) {
      const firstKey = `${menusData[0].id}`;
      const favoriteSection = menusData[0].favorite_section
      const secondKey = `${favoriteSection[0].id}`;
      setActiveKes([firstKey, secondKey])
    };

    const addresses: IDeliveryAddress[] = getLocalStorageJson(Constants.DELIVERY_ADDRESS) || [];
    let showContent = false;

    if (qrId) {
      const { tableDetails, qrCodeDetails } = await getTableAndQRCodeDetails(businessID, qrId);
      setDiningMethod(IDiningMethods.DINE_IN)
      if (tableDetails) {
        setTableDetails(tableDetails);
        setShowOption(true);
        showContent = true;
      } else {
        setErrorMsg(t('invalidOrTips'));
        if (!qrCodeDetails) {
          setErrorMsg(t('invalidOrTips2'));
        }
      }
    } else {
      const takeawayOptions = getLocalStorageJson(Constants.TAKEAWAY_OPTIONS) || [];
      if (Object.keys(takeawayOptions).length > 0) {
        setDiningMethod(IDiningMethods.TAKEAWAY);
        setPickUpOption(takeawayOptions);
        setShowOption(true);
        showContent = true;
      }
    }

    setShowContent(showContent);
    setAddressList(addresses);
    setLoading(false);
  }

  const handleConfirm = (option?: any) => {

    switch (diningMethod) {
      // case IDiningMethods.DELIVERY:
      //   if (option) {
      //     const newAddress = [...addressList];
      //     newAddress.unshift(option);
      //     setAddressList(newAddress);
      //     setLocalStorageJson(Constants.DELIVERY_ADDRESS, newAddress);
      //     if (newAddress.length > 0 && !showOption) {
      //       setShowOption(true);
      //     };
      //     setSelectedAddress(option);
      //   } else {
      //     if (addressList.length > 0) {
      //       setSelectedAddress(addressList[0]);
      //     }
      //   }
      //   break;
      case IDiningMethods.TAKEAWAY:
        if (option) {
          setPickUpOption(option);
          setShowOption(true);
          setLocalStorageJson(Constants.TAKEAWAY_OPTIONS, option);
        }
        break;
      case IDiningMethods.DINE_IN:
        if (option) {
          setTableDetails(option);
          setShowOption(true);
          setShowTips(true);
        }
        break;
      default:
        break;
    };
    setShowOption(true)
    setShowPopup(false);
    setShowContent(true);
  };

  const handleChangeAddress = (n: number) => {
    if (n < 0 || n >= addressList.length) return;
    const newList = [...addressList];
    const item = newList.splice(n, 1)[0];
    newList.unshift(item);
    setAddressList(newList);
    setLocalStorageJson(Constants.DELIVERY_ADDRESS, newList);
    setShowPopup(false);
  };

  const changeType = (type: IDiningMethods) => {
    switch (type) {
      case IDiningMethods.DELIVERY:
        if (addressList.length < 1) {
          setShowOption(false);
        }
        break;
      case IDiningMethods.TAKEAWAY:
        if (!pickUpOption) {
          setShowOption(false);
        }
        break;
      case IDiningMethods.DINE_IN:
        if (!tableDetails) {
          setShowOption(false);
        }
        break;

      default:
        break;
    }
    setDiningMethod(type);
  };

  const handleAddShoppingCart = (favorite: IFavorite) => {
    dispatch({
      type: ActionType.ADD_TO_SHOPPING_CART,
      storeID: businessStoreID,
      operate: 'ADD',
      favorite: favorite,
    });
  };

  const handleMinusShoppingCart = (favorite: IFavorite) => {
    dispatch({
      type: ActionType.ADD_TO_SHOPPING_CART,
      storeID: businessStoreID,
      operate: 'MINUS',
      favorite: favorite,
    });
  };

  const isCheckoutSubmittingRef = useRef(false);

  const handleCheckout = async () => {
    if (isCheckoutSubmittingRef.current) return;

    isCheckoutSubmittingRef.current = true;
    try {
      if (diningMethod !== undefined && shoppingCartData) {
        const order = getOrderByShoppingCart({
          type: diningMethod,
          businessID,
          shoppingCartData,
          table: tableDetails,
          deliveryAddress: addressList[0],
          pickUpOption,
          language: i18n.language,
        });

        const resp: any = await overbooking(businessID, order);
        if (resp && resp.code === 200 && resp.data && resp.data.payment_link) {
          const { payment_link } = resp.data;
          setShowShoppingCartPopup(false);
          window.location.href = `${payment_link}`;
        } else {
          Notify.show({ type: 'danger', message: resp?.message || t('order_failed') });
        }
      }
    } finally {
      isCheckoutSubmittingRef.current = false;
    }
  };

  const getDisabled = useMemo(() => {
    let disabled = true;

    if (
      (diningMethod === IDiningMethods.TAKEAWAY && pickUpOption) ||
      (diningMethod === IDiningMethods.DINE_IN && tableDetails)
    ) {
      disabled = false;
    }

    return disabled;
  }, [diningMethod, tableDetails, pickUpOption]);

  const handleChangeDiningMethod = (type: IDiningMethods) => {
    setDiningMethod(type);
    setOpenPopupRequest((n) => n + 1);
  };

  const handleExitConfirm = () => {
    dispatch({
      type: ActionType.CLEAR_SHOPPING_CART,
      storeID: businessStoreID,
    });
    setShowExitConfirm(false);
    setShowContent(false);
  };

  const handleContinueOrdering = () => {
    setShowExitConfirm(false);
  };

  return (
    <>
      <div className={`${prefix}`}>
        <Header />
        <div className={`${prefix}-content`}>
          <Business
            t={t}
            loading={loading}
            business={business}
            showBtn={showContent}
            onBack={() => setShowExitConfirm(true)}
          />
          <DiningMethodsPanel
            t={t}
            loading={loading}
            showOption={showOption}
            table={tableDetails}
            type={diningMethod}
            addressList={addressList}
            pickUpOption={pickUpOption}
            showContent={showContent}
            onClick={() => {
              if (diningMethod !== undefined) setShowPopup(true);
            }}
          />
          <div className={`${prefix}-menus`}>
            {
              !loading && showContent &&
              <Tabs
                data={menusData}
                activeKes={activeKes}
                setActiveKes={setActiveKes}
              />
            }
            <MenuList
              isFirst={isFirst}
              data={data}
              loading={loading}
              showContent={showContent}
              shoppingCartData={shoppingCartData}
              handleMinusShoppingCart={handleMinusShoppingCart}
              handleAddShoppingCart={handleAddShoppingCart}
            />
          </div>
          {
            !loading && !showContent &&
            <>
              <div className={`${prefix}-choose`}>
                <div className={`${prefix}-choose-title`}>{t('selectService')}</div>
                <div className={`${prefix}-choose-item ${diningMethod === IDiningMethods.DINE_IN ? 'active' : ''}`} onClick={() => handleChangeDiningMethod(IDiningMethods.DINE_IN)}>
                  <div className={`${prefix}-choose-item-icon`}><DineInIcon size={42} /></div>
                  <div className={`${prefix}-choose-item-text`}>{t('dineIn')}</div>
                </div>
                <div className={`${prefix}-choose-item ${diningMethod === IDiningMethods.TAKEAWAY ? 'active' : ''}`} onClick={() => handleChangeDiningMethod(IDiningMethods.TAKEAWAY)}>
                  <div className={`${prefix}-choose-item-icon`}><TakeawayIcon size={42} /></div>
                  <div className={`${prefix}-choose-item-text`}>{t('takeaway')}</div>
                </div>
              </div>
            </>
          }

          <Footer />

          {
            showContent &&
            <ShoppingCartBar
              t={t}
              handleCheckout={handleCheckout}
              shoppingCartData={shoppingCartData}
              showShoppingCartPopup={showShoppingCartPopup}
              setShowShoppingCartPopup={setShowShoppingCartPopup}
            />
          }

          <DiningMethods
            t={t}
            visible={showPopup}
            type={diningMethod}
            types={[IDiningMethods.DINE_IN, IDiningMethods.TAKEAWAY]}
            businessID={businessID}
            addressList={addressList}
            pickUpOption={pickUpOption}
            setType={changeType}
            setAddressList={setAddressList}
            handleChangeAddress={handleChangeAddress}
            onConfirm={handleConfirm}
            onClose={() => setShowPopup(false)}
          />
          <ShoppingCartPopup
            t={t}
            visible={showShoppingCartPopup}
            data={cartList}
            type={diningMethod}
            disabled={getDisabled}
            shoppingCartData={shoppingCartData}
            handleCheckout={handleCheckout}
            handleMinusShoppingCart={handleMinusShoppingCart}
            handleAddShoppingCart={handleAddShoppingCart}
            onClearCart={() => {
              dispatch({
                type: ActionType.CLEAR_SHOPPING_CART,
                storeID: businessStoreID,
              });
            }}
            onClose={() => setShowShoppingCartPopup(false)}
          />
        </div>
      </div>
      <Toast
        show={showTips}
        icon={<SuccessIcon />}
        setShow={setShowTips}
        message={t('seatedTips')}
      />
      <Popup
        visible={showExitConfirm}
        round
        closeable={false}
        position="center"
        onClose={handleContinueOrdering}
        className={`${prefix}-exit-confirm-popup`}
      >
        <div className={`${prefix}-exit-confirm`}>
          <TipIcon />
          <div className={`${prefix}-exit-confirm-title`}>{t('exitOrderConfirm')}</div>
          <div className={`${prefix}-exit-confirm-message`}>
            <div>{t('exitOrderMessage1')}</div>
            <div>{t('exitOrderMessage2')}</div>
          </div>
          <div className={`${prefix}-exit-confirm-buttons`}>
            <Button
              className={`${prefix}-exit-confirm-button-exit`}
              onClick={handleExitConfirm}
            >
              {t('exit')}
            </Button>
            <Button
              className={`${prefix}-exit-confirm-button-continue`}
              type="primary"
              onClick={handleContinueOrdering}
            >
              {t('continueOrdering')}
            </Button>
          </div>
        </div>
      </Popup>
      <Popup
        visible={showErrorPopup}
        round
        closeable={false}
        position="center"
        onClose={() => {
          setShowErrorPopup(false);
          setErrorMsg('');
        }}
        className={`${prefix}-error-popup`}
      >
        <div className={`${prefix}-error`}>
          <ScanCodeFailedIcon />
          <div className={`${prefix}-error-message`}>
            {errorMsg.split('\n').map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </div>
        </div>
      </Popup>
    </>
  )
}

export default MenuListPage;